/**
 * Public pricing page — pulls live tier data from `/api/marketing/pricing`.
 *
 * Admin manages tier names, prices, storage limits, features, and CTAs from
 * /app/settings/marketing. Each feature row supports a "Coming soon" / "New"
 * / "Beta" badge so visitors see what's live vs in flight.
 *
 * Visual style intentionally unchanged from the previous hardcoded layout —
 * only the data source moved server-side.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, HardDrive } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import api from "@/lib/api";

const BADGE_LABEL = {
  coming_soon: "Coming soon",
  new: "New",
  beta: "Beta",
};

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default to annual — biggest savings, what we want to anchor on.
  const [billingPeriod, setBillingPeriod] = useState("annual");

  useEffect(() => {
    let alive = true;
    api.get("/marketing/pricing")
      .then((r) => alive && setTiers(r.data || []))
      .catch(() => alive && setTiers([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />
      <section className="bloq-section">
        <div className="bloq-container">
          <div className="text-center mb-12 bloq-fade-up">
            <p className="bloq-eyebrow">Pricing</p>
            <h1 className="bloq-display text-5xl sm:text-6xl mt-3 mb-5">
              Built for solos. <span className="bloq-gradient-text">Priced for teams.</span>
            </h1>
            <p className="text-[#4a4a52] text-lg max-w-2xl mx-auto leading-relaxed">
              Bloq Free isn't a teaser — it's a complete workspace. Upgrade when you outgrow it.
            </p>
          </div>

          <BillingPeriodToggle value={billingPeriod} onChange={setBillingPeriod} />

          {loading ? (
            <p className="text-center text-[#82828c]">Loading…</p>
          ) : (
            <>
              {/* Paid tiers — 4-up grid. Free tier rendered below as a
                  landscape CTA banner so it doesn't compete visually
                  with the paid plans. */}
              <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 gap-5" data-testid="pricing-tiers">
                {tiers.filter((t) => t.tier_id !== "free" && t.price_monthly !== 0).map((t) => (
                  <PricingCard key={t.tier_id} tier={t} billingPeriod={billingPeriod} />
                ))}
              </div>
              {tiers.filter((t) => t.tier_id === "free" || t.price_monthly === 0).map((t) => (
                <FreeTierBanner key={t.tier_id} tier={t} />
              ))}
            </>
          )}

          <FullFeatureComparison billingPeriod={billingPeriod} />

          <div className="mt-20 text-center">
            <p className="text-sm text-[#82828c]">
              Need a feature that isn't on the roadmap yet?{" "}
              <Link to="/fund-a-feature" className="text-[#005e7a] underline font-medium">
                Fund it →
              </Link>
            </p>
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

export function BillingPeriodToggle({ value, onChange }) {
  const isAnnual = value === "annual";
  // Force the active button + outer track to share identical pill
  // radii via inline style — Tailwind's `rounded-full` was rendering
  // the inner button with visibly different curvature than the
  // outer cream track in some browsers, making it look like a square
  // chip sitting inside a pill. Inline 9999px is bulletproof.
  const activeBtn = "bg-[#005e7a] text-white shadow-sm";
  const inactiveBtn = "text-[#82828c] hover:text-[#1a1a1f]";
  const pill = { borderRadius: 9999 };
  return (
    <div className="flex justify-center mb-10" data-testid="billing-period-toggle">
      <div className="inline-flex items-center bg-[#f5efe6] p-1.5 gap-1" style={pill}>
        <button
          type="button"
          onClick={() => onChange("monthly")}
          style={pill}
          className={`px-5 py-2 text-sm font-medium transition-all ${
            !isAnnual ? activeBtn : inactiveBtn
          }`}
          aria-pressed={!isAnnual}
          data-testid="billing-toggle-monthly"
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => onChange("annual")}
          style={pill}
          className={`px-5 py-2 text-sm font-medium transition-all inline-flex items-center gap-2 ${
            isAnnual ? activeBtn : inactiveBtn
          }`}
          aria-pressed={isAnnual}
          data-testid="billing-toggle-annual"
        >
          Annual
          <span
            className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5"
            style={{
              borderRadius: 9999,
              background: isAnnual ? "rgba(255,255,255,0.2)" : "rgba(34,197,94,0.15)",
              color: isAnnual ? "#ffffff" : "#15803d",
            }}
          >
            Save
          </span>
        </button>
      </div>
    </div>
  );
}

function formatPrice(amount) {
  if (amount == null) return null;
  if (Number.isInteger(amount)) return String(amount);
  // Trim trailing zeros so $7.50 stays $7.50 but $7.00 becomes $7.
  const fixed = amount.toFixed(2);
  return fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
}

export function PricingCard({ tier, billingPeriod }) {
  const isCustom = tier.price_monthly == null;
  const isFree = tier.price_monthly === 0;
  const isAnnual = billingPeriod === "annual";

  // Pick the displayed per-month price based on the active toggle.
  // For "annual" we prefer the admin-set marketing number, falling back
  // to a clean per-12 split of price_yearly.
  let displayPrice = null;
  let secondaryLine = null;
  if (!isCustom && !isFree) {
    if (isAnnual && tier.price_yearly != null && tier.price_yearly > 0) {
      const adminMonthly = tier.annual_monthly_price;
      displayPrice = adminMonthly != null ? adminMonthly : (tier.price_yearly / 12);
      const savingsPct = Math.round((1 - tier.price_yearly / (tier.price_monthly * 12)) * 100);
      secondaryLine = `Billed annually at $${tier.price_yearly}${savingsPct > 0 ? ` · save ${savingsPct}%` : ""}`;
    } else {
      displayPrice = tier.price_monthly;
      secondaryLine = "Billed monthly";
    }
  }
  const priceLabel = isCustom ? "Custom" : isFree ? "Free" : `$${formatPrice(displayPrice)}`;

  // The savings phrase is rendered as a teal+bold inline span so it
  // pops out of the otherwise muted "Billed annually at $X · save Y%"
  // line. Built here so the JSX below stays clean.
  let secondaryNode = null;
  if (secondaryLine && isAnnual && tier.price_yearly != null && tier.price_yearly > 0) {
    const savingsPct = Math.round((1 - tier.price_yearly / (tier.price_monthly * 12)) * 100);
    if (savingsPct > 0) {
      secondaryNode = (
        <>
          Billed annually at ${tier.price_yearly} ·{" "}
          <span className="text-[#005e7a] font-bold">save {savingsPct}%</span>
        </>
      );
    } else {
      secondaryNode = `Billed annually at $${tier.price_yearly}`;
    }
  } else if (secondaryLine) {
    secondaryNode = secondaryLine;
  }

  return (
    <div
      className={`bloq-card p-7 flex flex-col gap-5 ${tier.highlight ? "ring-2 ring-[#005e7a] relative" : ""}`}
      data-testid={`tier-${tier.tier_id}`}
    >
      {tier.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#005e7a] text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-semibold">
          Recommended
        </span>
      )}
      <div>
        <p className="bloq-headline text-2xl text-[#005e7a]">{tier.name}</p>
        {tier.tagline && <p className="text-xs text-[#82828c] mt-1.5 leading-snug">{tier.tagline}</p>}
      </div>
      <div>
        <div className="flex items-baseline gap-1">
          <p className="text-4xl font-bold" data-testid={`tier-${tier.tier_id}-price`}>
            {priceLabel}
          </p>
          {!isCustom && !isFree && <span className="text-sm text-[#82828c]">/user/mo</span>}
        </div>
        {secondaryNode && (
          <p className="text-xs text-[#82828c] mt-1" data-testid={`tier-${tier.tier_id}-secondary`}>
            {secondaryNode}
          </p>
        )}
      </div>

      {tier.storage_gb != null && (
        <div className="flex items-center gap-2 text-xs text-[#4a4a52] bg-[#f5efe6] px-3 py-1.5 rounded-md self-start">
          <HardDrive className="w-3.5 h-3.5 text-[#005e7a]" />
          <span><strong>{tier.storage_gb} GB</strong> storage</span>
        </div>
      )}

      <ul className="space-y-2.5 flex-1">
        {(tier.features || []).map((f, i) => {
          const includedColor = f.included === false ? "text-[#c4c4cb]" : "text-[#005e7a]";
          const Icon = f.included === false ? X : Check;
          return (
            <li key={i} className="flex items-start gap-2 text-sm leading-snug">
              <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${includedColor}`} />
              <span className={f.included === false ? "text-[#82828c] line-through" : "text-[#1a1a1f]"}>
                {f.label}
                {f.badge && BADGE_LABEL[f.badge] && (
                  <span className="ml-2 inline-block bloq-pill text-[9px] uppercase tracking-wider bg-[#fef3c7] text-[#b45309] align-middle">
                    {BADGE_LABEL[f.badge]}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <Link
        to={tier.cta_url || "/beta-disclaimer"}
        className={`mt-2 ${tier.highlight ? "bloq-btn-primary" : "bloq-btn-secondary"} text-sm inline-flex items-center justify-center gap-2`}
        data-testid={`tier-cta-${tier.tier_id}`}
      >
        {tier.cta_label || "Choose"} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

/**
 * Free tier — rendered as a landscape banner BELOW the paid grid so
 * it doesn't compete visually with the upgrade tiers. Backend stays
 * unchanged — same `pricing_tiers` row, same admin editor; we just
 * pick it out of the tiers array and render this layout for it.
 */
export function FreeTierBanner({ tier }) {
  const features = (tier.features || []).filter((f) => f.included !== false).slice(0, 4);
  return (
    <div
      className="mt-8 bloq-card p-7 sm:p-8 flex flex-col md:flex-row items-stretch gap-6 md:items-center"
      data-testid={`tier-${tier.tier_id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-[#005e7a] font-semibold mb-1.5">
          Not ready to jump all the way in yet?
        </p>
        <h3 className="bloq-headline text-2xl sm:text-3xl">
          Try our generous <span className="text-[#005e7a]">free</span> plan
        </h3>
        {tier.tagline && (
          <p className="text-sm text-[#4a4a52] mt-2 leading-relaxed">{tier.tagline}</p>
        )}
        {features.length > 0 && (
          <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-1.5 mt-4">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#1a1a1f]">
                <Check className="w-4 h-4 text-[#005e7a] shrink-0 mt-0.5" />
                <span className="leading-snug">{f.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-col items-start md:items-end gap-3 md:min-w-[200px]">
        <div className="text-right">
          <p
            className="text-4xl font-bold text-[#1a1a1f]"
            data-testid={`tier-${tier.tier_id}-price`}
          >
            Free
          </p>
          {tier.storage_gb != null && (
            <p className="text-xs text-[#82828c] mt-1.5">
              Includes <strong>{tier.storage_gb} GB</strong> of storage
            </p>
          )}
        </div>
        <Link
          to={tier.cta_url || "/beta-disclaimer"}
          className="bloq-btn-secondary text-sm inline-flex items-center justify-center gap-2 whitespace-nowrap"
          data-testid={`tier-cta-${tier.tier_id}`}
        >
          {tier.cta_label || "Start free"} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Full Feature Comparison — backend-powered editable matrix. Decoupled
// from `pricing_tiers` (those drive the cards above). Lives in
// `pricing_matrix_plans` + `pricing_matrix_rows`; admins edit it from
// /app/settings/marketing → Comparison matrix tab.
//
// Render rules per the spec:
//   type:"included" → Check icon  (sr-only "Included")
//   type:"excluded" → X icon      (sr-only "Not included")
//   type:"text"     → render `value` as-is
// ---------------------------------------------------------------------------
function FullFeatureComparison({ billingPeriod }) {
  const [data, setData] = useState(null); // { plans, rows } | null
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/marketing/pricing-matrix")
      .then((r) => alive && setData(r.data || { plans: [], rows: [] }))
      .catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, []);

  if (failed) return null; // Fail silently — never break the cards above.
  const plans = data?.plans || [];
  const rows = data?.rows || [];

  return (
    <section className="mt-24 sm:mt-28" data-testid="full-feature-comparison">
      <div className="text-center mb-10 bloq-fade-up">
        <p className="bloq-eyebrow">Side by side</p>
        <h2 className="bloq-display text-4xl sm:text-5xl mt-3 mb-4">
          Full Feature <span className="bloq-gradient-text">Comparison</span>
        </h2>
        <p className="text-[#4a4a52] max-w-xl mx-auto leading-relaxed">
          Compare what&rsquo;s included across every Bloq plan.
        </p>
      </div>

      {!data ? (
        <p className="text-center text-[#82828c] py-8" data-testid="matrix-loading">Loading matrix…</p>
      ) : plans.length === 0 || rows.length === 0 ? (
        <p className="text-center text-[#82828c] py-8" data-testid="matrix-empty">
          The comparison matrix is coming soon.
        </p>
      ) : (
        <div className="bloq-card p-2 sm:p-3 overflow-hidden">
          <div className="overflow-x-auto" data-testid="matrix-scroll">
            <table className="w-full border-collapse text-sm" data-testid="matrix-table">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 z-10 bg-white text-left px-4 py-4 align-bottom min-w-[160px] sm:min-w-[200px] border-b border-[#e5e0d6]"
                  >
                    <span className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold">
                      Feature
                    </span>
                  </th>
                  {plans.map((p) => (
                    <MatrixPlanHeader
                      key={p.plan_id}
                      plan={p}
                      billingPeriod={billingPeriod}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={row.row_id}
                    className={idx % 2 === 0 ? "bg-[#fbfcfc]" : "bg-white"}
                    data-testid={`matrix-row-${row.row_id}`}
                  >
                    <th
                      scope="row"
                      className={`sticky left-0 z-10 text-left px-4 py-3.5 font-medium text-[#1a1a1f] border-b border-[#f0eadc] ${
                        idx % 2 === 0 ? "bg-[#fbfcfc]" : "bg-white"
                      }`}
                    >
                      {row.feature_name}
                    </th>
                    {plans.map((p) => (
                      <td
                        key={p.plan_id}
                        className="px-4 py-3.5 text-center text-[#4a4a52] border-b border-[#f0eadc] whitespace-normal"
                        data-testid={`matrix-cell-${row.row_id}-${p.slug}`}
                      >
                        <MatrixCellValue cell={row.values?.[p.slug]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function MatrixPlanHeader({ plan, billingPeriod }) {
  const isAnnual = billingPeriod === "annual";
  const isFree = !plan.monthly_price && plan.monthly_price !== 0
    ? false
    : plan.monthly_price === 0;
  // Pick which price to highlight based on the toggle. We always show
  // both lines for paid plans so visitors can quickly cross-reference.
  let primary = "Free";
  let secondary = null;
  if (!isFree) {
    if (isAnnual && plan.annual_equivalent_price != null) {
      primary = `$${plan.annual_equivalent_price}/user/mo annually`;
      secondary = `or $${plan.monthly_price}/user/mo monthly`;
    } else if (plan.monthly_price != null) {
      primary = `$${plan.monthly_price}/user/mo monthly`;
      if (plan.annual_equivalent_price != null) {
        secondary = `or $${plan.annual_equivalent_price}/user/mo annually`;
      }
    }
  }
  return (
    <th
      scope="col"
      className="px-4 py-4 text-center align-bottom min-w-[110px] sm:min-w-[130px] border-b border-[#e5e0d6]"
      data-testid={`matrix-plan-${plan.slug}`}
    >
      <p className="bloq-headline text-base sm:text-lg text-[#005e7a] leading-tight">
        {plan.name}
      </p>
      <p className="text-xs font-semibold text-[#1a1a1f] mt-1.5">{primary}</p>
      {secondary && (
        <p className="text-[10px] text-[#82828c] mt-0.5 leading-snug">
          {secondary}
        </p>
      )}
    </th>
  );
}

function MatrixCellValue({ cell }) {
  if (!cell || typeof cell !== "object") {
    return <span className="text-[#c4c4cb]" aria-hidden="true">—</span>;
  }
  if (cell.type === "included") {
    return (
      <>
        <Check
          className="w-5 h-5 text-[#15803d] mx-auto"
          aria-hidden="true"
          strokeWidth={2.5}
        />
        <span className="sr-only">Included</span>
      </>
    );
  }
  if (cell.type === "excluded") {
    return (
      <>
        <X
          className="w-5 h-5 text-[#c4c4cb] mx-auto"
          aria-hidden="true"
          strokeWidth={2}
        />
        <span className="sr-only">Not included</span>
      </>
    );
  }
  // text — render value as-is (may be "", numeric string, or descriptor).
  return <span className="text-xs sm:text-sm">{cell.value || ""}</span>;
}
